import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  AudioLines, Bell, Bot, Brain, CalendarClock, Check, CheckSquare, ChevronDown, ChevronUp,
  CreditCard, Globe, Loader2, Mail, MessageSquare, Mic, Sparkles, Star, Sunrise, Undo2, Zap,
} from 'lucide-react'
import {
  api, MeData, paymentApi, PayableTier, PlanKey, PricingPlan, SubscriptionInfo, SubscriptionTier,
} from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { fmtRub, fmtStars, perMonth, usePricing } from '../hooks/usePricing'
import { useTelegram } from '../hooks/useTelegram'
import WebApp from '@twa-dev/sdk'

// ── Tier config ───────────────────────────────────────────────────────────────

type BillingPeriod = 'month' | 'year'

// Цены здесь НЕ хранятся — они приходят из /api/subscription/pricing, то есть
// из backend/app/tariffs.py. В карточке остаётся только то, что цены не
// касается: название, состав тарифа, пути инвойсов.

// Применяет скидку промокода к сумме — как бэкенд (round, минимум 1).
// Реальная сумма к списанию всё равно приходит с сервера, здесь только показ.
function discounted(amount: number, pct: number): number {
  return Math.max(1, Math.round(amount * (1 - pct / 100)))
}

type FeatureIcon = typeof Check

interface FeatureDef {
  text: string
  Icon: FeatureIcon
}

interface TierDef {
  id: SubscriptionTier
  name: string
  planKey: PlanKey        // ключ месячного плана в /pricing
  planKeyYearly: PlanKey  // ключ годового плана
  popular?: boolean
  invoicePath: string
  invoicePathYearly: string
  features: FeatureDef[]
}

const UPGRADE_TIERS: TierDef[] = [
  {
    id: 'plus',
    name: 'Plus',
    planKey: 'plus',
    planKeyYearly: 'plus_yearly',
    invoicePath: '/api/subscription/invoice/plus',
    invoicePathYearly: '/api/subscription/invoice/plus_yearly',
    features: [
      { text: '40 сообщений в день',                 Icon: MessageSquare },
      { text: 'До 10 голосовых в день',              Icon: Mic           },
      { text: 'Почта и календарь по запросу',        Icon: Mail          },
      { text: 'События и задачи уходят в календарь', Icon: CalendarClock },
      { text: 'Веб-поиск',                           Icon: Globe         },
      { text: 'До 20 голосовых ответов в месяц',     Icon: Mic           },
      { text: 'Память на 300 фактов',                Icon: Brain         },
      { text: 'До 100 напоминаний и задач',          Icon: Bell          },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    planKey: 'pro',
    planKeyYearly: 'pro_yearly',
    popular: true,
    invoicePath: '/api/subscription/invoice/pro',
    invoicePathYearly: '/api/subscription/invoice/pro_yearly',
    features: [
      { text: 'Сам нахожу договорённости в почте',     Icon: Mail          },
      { text: 'Сам нахожу договорённости в чатах',     Icon: MessageSquare },
      { text: 'Покажу удалённое и изменённое в чате',  Icon: Undo2         },
      { text: 'Расшифровка голосовых в переписке',     Icon: AudioLines    },
      { text: 'Утренний разбор дня',                   Icon: Sunrise       },
      { text: 'Автоответчик, когда занят',             Icon: Bot           },
      { text: '300 сообщений в день',                  Icon: Zap           },
      { text: 'Память на 5000 фактов',                 Icon: Brain         },
      { text: 'Напоминания и задачи без ограничений',  Icon: Bell          },
    ],
  },
]

const TIER_ORDER: Record<SubscriptionTier, number> = { free: 0, plus: 1, pro: 2 }

const TIER_DISPLAY: Record<SubscriptionTier, string> = {
  free: 'Free', plus: 'Plus', pro: 'Pro',
}

const PAYMENT_TOAST = 'Тариф обновлён. Попробуй голосовой ответ или подключи Gmail.'

// ── Sub-components ────────────────────────────────────────────────────────────

function TierCard({
  tier,
  currentTier,
  period,
  onBuyStars,
  onBuyCard,
  isPending,
  cardAvailable,
  highlighted,
  remainingDays,
  discountPercent,
  discountTier,
  yearlyDiscountLabel,
  plan,
  planYearly,
}: {
  tier: TierDef
  currentTier: SubscriptionTier
  period: BillingPeriod
  onBuyStars: (path: string) => void
  onBuyCard: (tier: PayableTier) => void
  isPending: boolean
  cardAvailable: boolean
  highlighted?: boolean
  remainingDays?: number
  discountPercent?: number | null
  discountTier?: string | null
  yearlyDiscountLabel: string
  plan: PricingPlan | undefined
  planYearly: PricingPlan | undefined
}) {
  const currentOrder = TIER_ORDER[currentTier]
  const tierOrder = TIER_ORDER[tier.id]
  const isActive = currentTier === tier.id
  const isLower = tierOrder < currentOrder  // same tier is buyable (days stack)
  const canBuy = !isLower
  const isPro = tier.id === 'pro'
  const isYear = period === 'year'

  const activePlan = isYear ? planYearly : plan

  // Скидка промокода применяется, если она есть и относится к этому тарифу
  // (discountTier == null → на любой тариф). Показываем зачёркнутую старую цену.
  const discountApplies =
    !!discountPercent && discountPercent > 0 && canBuy &&
    (discountTier == null || discountTier === tier.id)
  const dc = (n: number) => (discountApplies ? discounted(n, discountPercent!) : n)

  // Цены приходят с сервера; пока их нет — карточка рисуется без чисел.
  const stars = activePlan?.stars
  const rub = activePlan?.rub
  // Yearly: show per-month with yearly total beneath; monthly: show total
  const mainStars = isYear && stars !== undefined ? perMonth(stars) : stars
  const mainRub   = isYear && rub   !== undefined ? perMonth(rub)   : rub

  const invoicePath = isYear ? tier.invoicePathYearly : tier.invoicePath
  const periodLabel = isYear ? '/ год' : '/ мес'
  const btnPeriod   = isYear ? '/год' : '/мес'

  const [showPulse, setShowPulse] = useState(false)
  useEffect(() => {
    if (isPro && canBuy) {
      const t = setTimeout(() => setShowPulse(true), 500)
      return () => clearTimeout(t)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro])

  const containerClass = isPro && highlighted
    ? 'bg-surface-1 border-2 border-accent p-6 shadow-[0_8px_48px_rgba(74,108,247,0.32)] ring-4 ring-accent/30'
    : isPro
    ? 'bg-surface-1 border-2 border-accent p-6 shadow-[0_8px_40px_rgba(74,108,247,0.22)] ring-4 ring-accent/10'
    : highlighted
    ? 'bg-surface-1 border-2 border-accent ring-4 ring-accent/20 shadow-lg p-5'
    : tier.popular
    ? 'bg-surface-1 border-2 border-accent p-5'
    : 'bg-surface-1 border border-line-1 p-5'

  return (
    <div className={`rounded-xl space-y-4 transition-all ${containerClass}`}>
      {isPro && !isActive && (
        <div className="flex items-center gap-1.5 -mt-2 mb-0">
          <Sparkles size={11} className="text-accent" />
          <span className="text-xs font-semibold text-accent uppercase tracking-wider">Максимум возможностей</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <p className={`font-display text-ink ${isPro ? 'text-xl' : 'text-lg'}`}>{tier.name}</p>
            {!isPro && tier.popular && (
              <span className="flex items-center gap-1 text-xs font-medium text-accent bg-accent-dim px-2 py-0.5 rounded-full">
                <Star size={10} /> Рекомендуем
              </span>
            )}
            {isYear && yearlyDiscountLabel && (
              <span className="text-xs font-medium text-success bg-success-dim px-2 py-0.5 rounded-full">
                {yearlyDiscountLabel}
              </span>
            )}
            {discountApplies && (
              <span className="text-xs font-semibold text-success bg-success-dim px-2 py-0.5 rounded-full">
                −{discountPercent}% промокод
              </span>
            )}
          </div>
          {/* Price line */}
          {mainStars !== undefined && mainRub !== undefined ? (
            <>
              <p className={`text-ink-1 ${isPro ? 'text-lg font-medium' : 'text-base'}`}>
                {fmtStars(dc(mainStars))}/мес
                {discountApplies && (
                  <span className="text-sm text-ink-3 line-through ml-2">{fmtStars(mainStars)}/мес</span>
                )}
                <span className="text-sm text-ink-2 ml-1.5">~{fmtRub(dc(mainRub))}</span>
              </p>
              {isYear && stars !== undefined && (
                <p className="text-xs text-ink-2 mt-0.5">
                  ({fmtStars(dc(stars))}/год)
                  {discountApplies && (
                    <span className="text-ink-3 line-through ml-1.5">({fmtStars(stars)}/год)</span>
                  )}
                </p>
              )}
            </>
          ) : (
            <p className="text-ink-2 text-base">Загружаем цены…</p>
          )}
        </div>
        {isActive && (
          <span className="text-xs font-medium text-success bg-success-dim px-2 py-0.5 rounded-full">
            Активен
          </span>
        )}
      </div>

      {/* Features with icons */}
      <div className="space-y-2.5">
        {tier.features.map(({ text, Icon }) => (
          <div key={text} className="flex items-center gap-2.5">
            <div
              className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
              style={{ background: 'var(--color-accent-dim)' }}
            >
              <Icon size={12} className="text-accent" />
            </div>
            <span className="text-sm text-ink-1">{text}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      {canBuy ? (
        <div className="space-y-2">
          <button
            onClick={() => onBuyStars(invoicePath)}
            disabled={isPending || stars === undefined}
            className={`btn-active w-full h-11 rounded-[10px] text-sm font-medium text-white transition-opacity disabled:opacity-40 flex items-center justify-center gap-2 ${
              showPulse ? 'btn-pulse-once' : ''
            }`}
            style={{ background: 'var(--color-accent-gradient)', boxShadow: '0 2px 14px rgba(74,108,247,0.32)' }}
          >
            <Star size={14} />
            {isPending
              ? 'Открываем...'
              : stars === undefined
              ? 'Загружаем цены…'
              : `${isActive ? 'Продлить — ' : ''}${fmtStars(dc(stars))}${btnPeriod}`}
          </button>
          {isActive && typeof remainingDays === 'number' && (
            <p className="text-xs text-ink-2 text-center">
              Дни добавятся к текущим {remainingDays} дням
            </p>
          )}
          {cardAvailable && (
            <button
              onClick={() => onBuyCard(isYear ? `${tier.id}_yearly` as PayableTier : tier.id)}
              disabled={isPending || rub === undefined}
              className="btn-active w-full h-11 rounded-[10px] text-sm font-medium border border-line-2 bg-surface-2 text-ink transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <CreditCard size={14} className="text-ink-1" />
              {rub === undefined
                ? 'Загружаем цены…'
                : `${isActive ? 'Продлить картой' : 'Картой'} — ${fmtRub(dc(rub))} ${periodLabel}`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-xs text-ink-2 text-center py-1">У тебя тариф выше</p>
      )}
    </div>
  )
}

function ActivePlanView({
  sub,
  me,
}: {
  sub: SubscriptionInfo
  me: MeData | undefined
}) {
  const tier = sub.tier
  const usedMsg = me?.usage.text_messages_used ?? 0
  const limitMsg = me?.usage.text_messages_limit ?? null
  const usedVoice = me?.usage.voice_messages_used ?? 0
  const voiceLimit = me?.usage.voice_messages_limit ?? null

  return (
    <div className="space-y-4">
      {/* Current plan banner */}
      <div className="bg-surface-1 border border-line-1 rounded-xl p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-full bg-accent-dim flex items-center justify-center shrink-0">
          <Sparkles size={20} className="text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="font-display text-base text-ink">Delo {TIER_DISPLAY[tier]}</p>
            <span className="text-xs font-medium text-success bg-success-dim px-2 py-0.5 rounded-full">Активен</span>
          </div>
          {sub.expires_at ? (
            <p className="text-sm text-ink-2">
              До {new Date(sub.expires_at).toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          ) : (
            <p className="text-sm text-ink-2">Бессрочная</p>
          )}
        </div>
      </div>

      {/* Usage */}
      <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
        <p className="px-4 pt-3 pb-2 text-xs font-medium text-ink-2 uppercase tracking-wider">
          Использование
        </p>
        {limitMsg !== null && (
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-ink-1">Сообщений сегодня</p>
            <p className="text-sm font-medium text-ink tabular-nums">{usedMsg} / {limitMsg}</p>
          </div>
        )}
        {limitMsg === null && (
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-ink-1">Сообщений сегодня</p>
            <p className="text-sm font-medium text-ink tabular-nums">{usedMsg} / ∞</p>
          </div>
        )}
        <div className="px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-ink-1">Голосовых сегодня</p>
          <p className="text-sm font-medium text-ink tabular-nums">
            {usedVoice} / {voiceLimit === null ? '∞' : voiceLimit}
          </p>
        </div>

        {tier === 'pro' && me?.usage && (
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-ink-1">Фактов в памяти</p>
            <p className="text-sm font-medium text-ink tabular-nums">
              {me.usage.facts_used} / {me.usage.facts_limit ?? 5000}
            </p>
          </div>
        )}

      </div>
    </div>
  )
}

function PromoCodeSection({
  onApply,
  isPending,
}: {
  onApply: (code: string) => void
  isPending: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [code, setCode] = useState('')
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  const handleApply = () => {
    if (!code.trim()) return
    onApply(code.trim())
  }

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-center text-sm text-ink-2 flex items-center justify-center gap-1 py-2"
      >
        Есть промокод?
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {isOpen && (
        <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-3">
          <input
            type="text"
            value={code}
            onChange={e => {
              setCode(e.target.value.toUpperCase())
              setMessage(null)
            }}
            placeholder="Введи промокод"
            className="w-full h-11 px-4 rounded-lg border border-line-2 bg-surface-0 text-ink text-sm focus:outline-none focus:border-accent"
            disabled={isPending}
          />
          <button
            onClick={handleApply}
            disabled={!code.trim() || isPending}
            className="btn-active w-full h-10 rounded-lg text-sm font-medium text-accent bg-accent-dim disabled:opacity-40"
          >
            {isPending ? 'Проверяем...' : 'Применить'}
          </button>
          {message && (
            <p className={`text-sm text-center ${message.type === 'success' ? 'text-success' : 'text-error'}`}>
              {message.text}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function Subscription() {
  const { haptic } = useTelegram()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const initTier    = searchParams.get('tier') as SubscriptionTier | null
  const initSection = searchParams.get('section')
  const initPeriod  = searchParams.get('period') as BillingPeriod | null

  const { data: sub, isLoading: subLoading, error: subError, refetch } = useQuery<SubscriptionInfo>({
    queryKey: ['subscription'],
    queryFn: () => api.get('/api/subscription'),
    staleTime: 60_000,
  })

  const { data: me } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
    enabled: sub?.tier === 'plus' || sub?.tier === 'pro',
  })

  const { data: paymentAvailable } = useQuery({
    queryKey: ['payment-available'],
    queryFn: () => paymentApi.available(),
    staleTime: 5 * 60_000,
  })
  const cardAvailable = paymentAvailable?.available ?? false

  // Цены — с бэкенда (backend/app/tariffs.py), а не из констант этой страницы.
  const pricing = usePricing()

  const qc = useQueryClient()
  const [cardPolling, setCardPolling] = useState(false)
  const [pollingTimedOut, setPollingTimedOut] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const notify = (msg: string) => {
    try { WebApp.showAlert(msg) } catch { /* noop */ }
  }

  const refreshAll = () => {
    refetch()
    qc.invalidateQueries({ queryKey: ['me'] })
    qc.invalidateQueries({ queryKey: ['today'] })
  }

  const afterPaymentSuccess = () => {
    refreshAll()
    navigate('/', { state: { toast: PAYMENT_TOAST } })
  }

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    setCardPolling(false)
  }

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && cardPolling) refreshAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardPolling])

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current)
  }, [])

  useEffect(() => {
    if (!sub) return
    const targetId = initTier ? `tier-${initTier}` : null
    if (!targetId) return
    const t = setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 350)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub])

  const tierMutation = useMutation({
    mutationFn: (path: string) => api.post<{ invoice_url: string }>(path),
    onSuccess: ({ invoice_url }) => {
      WebApp.openInvoice(invoice_url, status => {
        if (status === 'paid') {
          afterPaymentSuccess()
        } else if (status === 'cancelled' || status === 'failed') {
          notify('Оплата не завершена.')
        }
      })
    },
    onError: () => notify('Не удалось открыть оплату. Попробуй ещё раз.'),
  })

  const cardMutation = useMutation({
    mutationFn: (tier: PayableTier) => paymentApi.create(tier),
    onSuccess: ({ confirmation_url, payment_id }) => {
      WebApp.openLink(confirmation_url)
      setCardPolling(true)
      setPollingTimedOut(false)
      let attempts = 0
      pollRef.current = setInterval(async () => {
        attempts++
        try {
          const st = await paymentApi.status(payment_id)
          if (st.status === 'succeeded') {
            stopPolling()
            afterPaymentSuccess()
          } else if (st.status === 'canceled') {
            stopPolling()
            notify('Платёж отменён.')
          } else if (attempts >= 40) {
            stopPolling()
            setPollingTimedOut(true)
            notify('Пока не получили подтверждение. Если деньги списались — тариф обновится автоматически.')
          }
        } catch {
          stopPolling()
        }
      }, 3000)
    },
    onError: () => notify('Не удалось создать платёж. Попробуй ещё раз.'),
  })


  const promoMutation = useMutation({
    mutationFn: (code: string) => api.post<{ valid: boolean; discount_percent?: number; days?: number; type: string }>('/api/payment/promo/check', { code }),
    onSuccess: (data) => {
      if (data.valid) {
        if (data.type === 'discount' && data.discount_percent) {
          notify(`Промокод применён — скидка ${data.discount_percent}%`)
        } else if (data.type === 'days' && data.days) {
          notify(`Промокод применён — +${data.days} дней`)
        }
        refreshAll()
      }
    },
    onError: () => notify('Промокод не найден или уже использован'),
  })

  const [period, setPeriod] = useState<BillingPeriod>(
    initPeriod === 'year' ? 'year' : 'month'
  )

  const currentTier: SubscriptionTier = sub?.tier ?? 'free'
  const isPaid = currentTier !== 'free'
  const anyPending =
    tierMutation.isPending || cardMutation.isPending || cardPolling
  const remainingDays = sub?.expires_at
    ? Math.max(0, Math.ceil((new Date(sub.expires_at).getTime() - Date.now()) / 86_400_000))
    : 0

  const handleBuyStars = (path: string) => {
    haptic.impactOccurred('medium')
    tierMutation.mutate(path)
  }

  const handleBuyCard = (tier: PayableTier) => {
    haptic.impactOccurred('medium')
    cardMutation.mutate(tier)
  }

  const handleApplyPromo = (code: string) => {
    haptic.impactOccurred('medium')
    promoMutation.mutate(code)
  }

  const handlePeriod = (p: BillingPeriod) => {
    haptic.impactOccurred('light')
    setPeriod(p)
  }

  const handleManualCheck = () => {
    setPollingTimedOut(false)
    refreshAll()
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Подписка</p>
        <p className="text-xs text-ink-2 mt-0.5">Без автопродления</p>
      </div>

      <div className="flex-1 px-4 pb-8 space-y-3">
        {subLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {subError && <ErrorCard onRetry={refetch} />}

        {cardPolling && (
          <div className="bg-surface-1 border border-line-1 rounded-lg p-4 flex items-center gap-3">
            <Loader2 size={18} className="text-accent shrink-0 animate-spin" />
            <p className="text-sm text-ink-1">
              Ждём подтверждение оплаты… Заверши оплату в открывшемся окне и вернись сюда.
            </p>
          </div>
        )}

        {pollingTimedOut && (
          <button
            onClick={handleManualCheck}
            className="btn-active w-full h-11 rounded-xl border border-line-2 bg-surface-1 text-sm font-medium text-accent flex items-center justify-center gap-2"
          >
            Проверить снова
          </button>
        )}

        {sub && (
          <>
            {/* Текущий тариф + использование */}
            {isPaid && (
              <ActivePlanView sub={sub} me={me} />
            )}

            {/* Period toggle */}
            <div className="relative flex rounded-xl border border-line-1 bg-surface-1 p-1">
              <div
                className="period-pill absolute top-1 bottom-1 rounded-lg pointer-events-none"
                style={{
                  width: 'calc(50% - 4px)',
                  left: 4,
                  // Сдвиг на собственную ширину = ровно вторая половина.
                  // На transform, а не на left: layout-свойства пересчитывают
                  // раскладку на каждом кадре.
                  transform: period === 'month' ? 'translateX(0)' : 'translateX(100%)',
                  background: 'var(--color-accent-gradient)',
                }}
              />
              <button
                onClick={() => handlePeriod('month')}
                className={`relative z-10 flex-1 py-2.5 text-sm font-medium transition-colors ${
                  period === 'month' ? 'text-white' : 'text-ink-2'
                }`}
              >
                Месяц
              </button>
              <button
                onClick={() => handlePeriod('year')}
                className={`relative z-10 flex-1 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  period === 'year' ? 'text-white' : 'text-ink-2'
                }`}
              >
                Год
                {pricing.yearlyDiscountLabel && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full transition-colors ${
                    period === 'year' ? 'bg-white/20 text-white' : 'bg-success-dim text-success'
                  }`}>
                    {pricing.yearlyDiscountLabel}
                  </span>
                )}
              </button>
            </div>

            {/* Карточки тарифов — все 3 тарифа всегда видны */}
            {UPGRADE_TIERS.map(t => (
              <div key={t.id} id={`tier-${t.id}`}>
                <TierCard
                  tier={t}
                  currentTier={currentTier}
                  period={period}
                  onBuyStars={handleBuyStars}
                  onBuyCard={handleBuyCard}
                  isPending={anyPending}
                  cardAvailable={cardAvailable}
                  highlighted={initTier === t.id}
                  remainingDays={remainingDays}
                  discountPercent={sub.discount_percent}
                  discountTier={sub.discount_tier}
                  yearlyDiscountLabel={pricing.yearlyDiscountLabel}
                  plan={pricing.plan(t.planKey)}
                  planYearly={pricing.plan(t.planKeyYearly)}
                />
              </div>
            ))}

            {/* Промокод */}
            <PromoCodeSection
              onApply={handleApplyPromo}
              isPending={promoMutation.isPending}
            />


            <p className="text-xs text-ink-2 text-center pt-2 pb-1">
              {cardAvailable
                ? 'Оплата через Telegram Stars или банковскую карту.'
                : 'Оплата через Telegram Stars.'}
            </p>

            <p className="text-[11px] text-ink-2 text-center leading-relaxed px-4 pb-1">
              Оплачивая подписку, вы принимаете условия{' '}
              <Link to="/offer" className="underline underline-offset-2">публичной оферты</Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
